import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CalendarDays, Link2, Loader2, X } from 'lucide-react';
import { api } from '../../services/api';

interface LinkableBooking {
  bookingId: string;
  customer: string;
  roomName: string;
  startDate: string;
  endDate: string;
  status: 'pending' | 'approved';
  facilitatorName: string;
  accountTeamLeadEmail: string | null;
  mine: boolean;
}

const fmtDay = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
const daysOf = (b: LinkableBooking) => {
  const out: string[] = [];
  for (let t = Date.parse(`${b.startDate}T00:00:00Z`); t <= Date.parse(`${b.endDate}T00:00:00Z`) && out.length < 31; t += 86400_000) out.push(new Date(t).toISOString().slice(0, 10));
  return out;
};
const errorOf = (e: any) => e?.response?.data?.error || e?.message || 'Something went wrong';

/**
 * Links one session to an upcoming studio booking. That starts the booking's
 * Session Hub pack and tells its account manager, who fills it in from My Bookings.
 */
export function LinkBookingModal({ projectId, sessionId, sessionName, onClose, onLinked }: {
  projectId: string;
  sessionId: string;
  sessionName: string;
  onClose: () => void;
  onLinked: () => void;
}) {
  const [bookings, setBookings] = useState<LinkableBooking[] | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [days, setDays] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ bookings: LinkableBooking[] }>(`/api/projects/${projectId}/linkable-bookings`)
      .then((r) => setBookings(r.data.bookings))
      .catch((e) => { setError(errorOf(e)); setBookings([]); });
  }, [projectId]);

  const booking = useMemo(() => bookings?.find((b) => b.bookingId === picked) ?? null, [bookings, picked]);
  const allDays = booking ? daysOf(booking) : [];

  const pick = (b: LinkableBooking) => {
    setPicked(b.bookingId);
    setDays(new Set(daysOf(b)));
  };
  const toggleDay = (d: string) => setDays((prev) => {
    const next = new Set(prev);
    if (next.has(d)) next.delete(d); else next.add(d);
    return next;
  });

  async function link() {
    if (!booking || !days.size) return;
    setBusy(true);
    setError(null);
    try {
      const body = { bookingId: booking.bookingId, days: days.size === allDays.length ? 'all' : [...days].sort() };
      await api.post(`/api/projects/${projectId}/sessions/${sessionId}/booking`, body);
      onLinked();
    } catch (e) {
      setError(errorOf(e));
    } finally {
      setBusy(false);
    }
  }

  const mine = (bookings ?? []).filter((b) => b.mine);
  const others = (bookings ?? []).filter((b) => !b.mine);

  const option = (b: LinkableBooking) => (
    <button
      key={b.bookingId} type="button" onClick={() => pick(b)}
      className={`flex items-center justify-between p-3 rounded-xl border text-left transition-all ${picked === b.bookingId ? 'border-primary bg-primary/5' : 'border-[#e2e8f0] hover:border-primary/40 hover:bg-slate-50'}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${picked === b.bookingId ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-400'}`}><CalendarDays size={14} /></div>
        <div>
          <p className={`text-sm font-semibold ${picked === b.bookingId ? 'text-navy' : 'text-slate-700'}`}>{b.customer}</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {b.startDate === b.endDate ? fmtDay(b.startDate) : `${fmtDay(b.startDate)} to ${fmtDay(b.endDate)}`} · {b.roomName} · {b.facilitatorName}
            {b.accountTeamLeadEmail ? ` · AM ${b.accountTeamLeadEmail}` : ' · no account manager'}
          </p>
        </div>
      </div>
      {b.status === 'pending' && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 flex-shrink-0">Pending</span>}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" role="dialog" aria-modal="true" aria-label="Link to booking" onClick={onClose}>
      <div className="w-full max-w-xl max-h-[85vh] bg-white rounded-2xl shadow-xl flex flex-col font-display" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-primary tracking-widest uppercase">Link to booking</p>
            <h2 className="text-lg font-bold text-navy truncate">{sessionName}</h2>
          </div>
          <button type="button" onClick={onClose} title="Close" className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>
        {error && (
          <div className="px-5 py-3 border-b border-[#e2e8f0] bg-red-50 flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" /><p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4">
          <p className="text-xs text-slate-500">Linking starts the booking's Session Hub pack and emails its account manager to fill in attendees, the sponsor, client email domains and retention.</p>
          {bookings === null ? (
            <div className="flex justify-center py-10 text-[#64748b]"><Loader2 size={20} className="animate-spin" /></div>
          ) : bookings.length === 0 ? (
            <p className="text-sm text-slate-400">No upcoming bookings.</p>
          ) : (
            <>
              {mine.length > 0 && <div className="flex flex-col gap-2"><p className="text-[11px] font-bold text-slate-400 tracking-widest uppercase">You're facilitating</p>{mine.map(option)}</div>}
              {others.length > 0 && <div className="flex flex-col gap-2"><p className="text-[11px] font-bold text-slate-400 tracking-widest uppercase">Other upcoming bookings</p>{others.map(option)}</div>}
            </>
          )}
          {booking && allDays.length > 1 && (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-navy">Which days does this session cover?</p>
              {allDays.map((d) => (
                <label key={d} className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={days.has(d)} onChange={() => toggleDay(d)} /> {fmtDay(d)}
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm">Cancel</button>
          <button type="button" onClick={link} disabled={!booking || !days.size || busy} className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />} Link
          </button>
        </div>
      </div>
    </div>
  );
}
